import {
  getShortcutModifiers,
  isDisabled,
  sentryComponentReporter,
  shortcutLabel
} from './helpers';

/*******************************************************************************
 * shortcutFocus */
/**
 * This directive allows for focusing on an element using keyboard shortcuts.
 * Typically, this will be used on an input field, though any element that can
 * be focused should work as well.
 *******************************************************************************/
export default function shortcutFocus() {
  /* @ngInject */ function focusController($element, $injector, $scope) {
    var $ctrl = this;
    var $svcs = { $ctrl, $element, $injector, $scope };

    $ctrl.$onInit = $injector.invoke(
      initCtrl,
      null,
      Object.assign($svcs, { $svcs })
    );
  }

  /*****************************************************************************
   * initCtrl */
  /**
   * Initializes the controller object with data and functions.
   *****************************************************************************/
  /* @ngInject */ function initCtrl(
    $ctrl,
    $document,
    $element,
    $injector,
    $scope,
    $svcs,
    $timeout,
    SHORTCUT_CONSTANTS
  ) {
    return function () {
      Object.assign($ctrl, {
        /*--------------------------------------------*/
        /* We're making targetKey a function so that  */
        /* any angular code in the attribute has time */
        /* to be converted to the expression's value. */
        /*--------------------------------------------*/
        getTargetKey: () =>
          $element[0].hasAttribute(SHORTCUT_CONSTANTS.ATTR_FOCUS)
            ? $element[0].getAttribute(SHORTCUT_CONSTANTS.ATTR_FOCUS)
            : null,
        isLabelHidden: () =>
          $element[0].hasAttribute(SHORTCUT_CONSTANTS.ATTR_HIDE_LABEL) &&
          $element[0].getAttribute(SHORTCUT_CONSTANTS.ATTR_HIDE_LABEL) !==
            'false',

        getShortcutModifiers: $injector.invoke(
          getShortcutModifiers,
          null,
          $svcs
        ),
        isDisabled: $injector.invoke(isDisabled, null, $svcs),
        keyListener: $injector.invoke(keyListener, null, $svcs),
        sentryComponentReporter: $injector.invoke(
          sentryComponentReporter,
          null,
          $svcs
        ),
        shortcutLabel: $injector.invoke(shortcutLabel, null, $svcs),
        updateText: $injector.invoke(updateText, null, $svcs)
      });

      Object.assign($ctrl, $ctrl.getShortcutModifiers());

      /*-----------------*/
      /* Set up listener */
      /*-----------------*/
      $document.on('keydown', $ctrl.keyListener);
      $scope.$on('$destroy', () => {
        $document.off('keydown', $ctrl.keyListener);
      });

      $timeout($ctrl.updateText, 50);
    };
  }

  /*****************************************************************************
   * keyListener */
  /**
   * Key press listener for the shortcut key.
   *
   * A delay was found to be needed when using this with confirm alert dialogs.
   *****************************************************************************/
  /* @ngInject */ function keyListener($ctrl, $element, $timeout, KEYCODES) {
    const element = $element[0];
    const TYPE_SELECT = 'SELECT';
    return function (event) {
      try {
        /*-------------------------------------------------------*/
        /* If the user clicked a key that is not defined, we do  */
        /* not want to continue. This has been seen in Microsoft */
        /* Edge when hitting Tab to autofill an email address.   */
        /*-------------------------------------------------------*/
        if (
          event.keyCode === null ||
          event.keyCode === undefined ||
          $ctrl.isDisabled()
        )
          return;

        /*----------------------------------------------*/
        /* Make sure if one or more of the modifier     */
        /* keys are required, that they are being used. */
        /*----------------------------------------------*/
        if ($ctrl.modAlt != event.altKey) return;
        if ($ctrl.modCtrl != event.ctrlKey) return;
        if ($ctrl.modShift != event.shiftKey) return;

        /*---------------------------------------------------*/
        /* Check that the pressed key matches the targetKey. */
        /*---------------------------------------------------*/
        const code = event.keyCode;
        const targetKey = ($ctrl.getTargetKey() || '').toUpperCase();
        if (KEYCODES.LETTERS[targetKey] !== code) {
          return;
        }

        event.preventDefault();

        const insideInput = element.querySelector('input:first-child');

        /*----------------------------------------------------------*/
        /* If this element is a wrapper for an input, we want to    */
        /* use the inner input. Otherwise, use the current element. */
        /*----------------------------------------------------------*/
        $timeout(() => {
          (insideInput || element).focus();

          /*--------------------------------------------------------------------*/
          /* In addition to the focus, we also want to open the dropdown for    */
          /* any selection input. This sometimes throws an error while          */
          /* debugging, referring to a needed gesture (actual human interaction */
          /* rather than programmatic). This seems to work just fine in normal   */
          /* use, so I don't know why it throws this error when debugging.      */
          /*--------------------------------------------------------------------*/
          if (element.nodeName == TYPE_SELECT) {
            (insideInput || element).showPicker();
          }
        }, 150);
      } catch (error) {
        $ctrl.sentryComponentReporter(error, { keyCodeEvent: event });
      }
    };
  }

  /*****************************************************************************
   * updateText */
  /**
   * Update the text with the shortcut value. Skipped if the shortcut-hide-label
   * attribute is true.
   *****************************************************************************/
  /* @ngInject */ function updateText($ctrl, $element) {
    const element = $element[0];

    /*---------------*/
    /* Element types */
    /*---------------*/
    const TYPE_INPUT = 'INPUT';
    const TYPE_LAZY_TYPEAHEAD = 'LAZY-TYPEAHEAD';
    const TYPE_SELECT = 'SELECT';

    return function () {
      if ($ctrl.isLabelHidden()) {
        return;
      }

      /*--------------------------------------------------------------------*/
      /* If the element is an input, we want to grab the Placeholder value. */
      /* However, if used on a dropdown selection, there is no placeholder. */
      /* Assume the first option should be used for the shortcut label.     */
      /*--------------------------------------------------------------------*/
      let currentPlaceholder = null;
      switch (element.nodeName) {
        case TYPE_INPUT:
          currentPlaceholder = element.placeholder;
          break;
        case TYPE_LAZY_TYPEAHEAD:
          currentPlaceholder = element.attributes.placeholder.value;
          break;
        case TYPE_SELECT:
          currentPlaceholder = element.options[0].label;
          break;
        default:
          console.error('Type unknown: ', element.nodeName);
      }

      const insideInput = element.querySelector('input:first-child');
      const shortcutText = `${currentPlaceholder} ${$ctrl.shortcutLabel()}`;
      switch (element.nodeName) {
        case TYPE_INPUT:
          element.placeholder = shortcutText;
          break;
        case TYPE_LAZY_TYPEAHEAD:
          insideInput.placeholder = shortcutText;
          break;
        case TYPE_SELECT:
          element.options[0].label = shortcutText;
          break;
      }
    };
  }

  /*------------------------------*/
  /* Return the directive object. */
  /*------------------------------*/
  return {
    restrict: 'A',
    controller: ['$element', '$injector', '$scope', focusController]
  };
}
